/**
 * Static geometry merge (E3.3.S2, AD-158 constraints 1 + 4 + 5;
 * docs/architecture/rendering-performance.md §2).
 *
 * The biome dressing clones one template per placement, and every clone's leaf
 * meshes are separate draw calls — Driftwell's 169 settlement pieces cost 1,447
 * calls in the S1 probe (AD-685) against 49 resident geometries. This merges the
 * PLAIN STATIC LEAF meshes of a placed group into one mesh per
 * (cell × material × vertex layout):
 *
 * - **Granularity (constraint 1).** A bucket never spans more than one
 *   `MERGE_CELL_METRES` square of the ground, so a merged mesh is a neighbourhood
 *   of a settlement — a handful of buildings — never the whole world (which would
 *   defeat frustum culling), and never one mesh per piece (which is today).
 * - **Behavioural nodes stay live (constraint 4).** `behaviourReason` is the
 *   exclusion list; a mesh under an excluded ancestor stays live too, because its
 *   world transform belongs to something that may move. Pinned by
 *   staticMerge.test.ts, including a mutation-style case per reason.
 * - **Amortised commit (constraint 5, scoped).** `StaticMergeQueue.step` commits
 *   buckets until its slice budget (≤ `MERGE_SLICE_MS`) is spent, one bucket
 *   minimum, so a large town cannot freeze a frame. The full off-thread Worker
 *   build is NOT taken here (Pillar 16): it is the named E3.6 demand trigger
 *   "a merge commit exceeds 8 ms in the S1 probe".
 *
 * Render-only: the collision world is the shared WR.2 source and never reads a
 * render mesh (AD-026/AD-047), so a merge cannot move a collider.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** Edge of the ground square one merged mesh may span (metres). */
export const MERGE_CELL_METRES = 32;
/** Per-frame commit budget (AD-158 §5: ≤ ~8 ms per frame slice). */
export const MERGE_SLICE_MS = 8;
/**
 * A mesh above this many vertices stays live. Merging saves ONE draw call per
 * member, and copying a high-poly member costs milliseconds: the first AFTER probe
 * (H-1141) measured a single Driftwell bucket commit at 413 ms with no cap — a
 * frame hitch no draw-call saving pays for. Low-poly kit pieces are where calls
 * are cheap to remove.
 */
export const MERGE_MAX_MESH_VERTICES = 4096;
/** A bucket is split before it passes this many vertices, which bounds one commit. */
export const MERGE_MAX_BUCKET_VERTICES = 32768;

/** Name fragments that mark a node as behavioural in authored glTF. */
const BEHAVIOUR_NAME =
  /collider|collision|trigger|interact|door|lever|lift|anim|light|emitter|particle/i;
/** `userData` flags an author or a system can set to keep a node live. */
const BEHAVIOUR_FLAGS = ['collider', 'interaction', 'animated', 'dynamic', 'noMerge'] as const;

/**
 * Why a node must stay a live scene object, or null for a node with no behaviour
 * of its own. Checked on the mesh AND every ancestor below the merge root.
 */
export function behaviourReason(node: THREE.Object3D): string | null {
  if ((node as THREE.Light).isLight) return 'light';
  if ((node as THREE.Points).isPoints) return 'particles';
  if ((node as THREE.SkinnedMesh).isSkinnedMesh) return 'skinned';
  if ((node as THREE.InstancedMesh).isInstancedMesh) return 'instanced';
  if ((node as THREE.LOD).isLOD) return 'lod';
  for (const flag of BEHAVIOUR_FLAGS) if (node.userData[flag]) return `userData.${flag}`;
  if (BEHAVIOUR_NAME.test(node.name)) return 'name';
  if (node.animations.length > 0) return 'animations';
  return null;
}

/** Why a MESH cannot join a bucket even with no behaviour (shape, not behaviour). */
function shapeReason(mesh: THREE.Mesh): string | null {
  if (Array.isArray(mesh.material)) return 'multi-material';
  if (mesh.children.length > 0) return 'has-children';
  if (Object.keys(mesh.geometry.morphAttributes).length > 0) return 'morph';
  if (mesh.geometry.getAttribute('position') === undefined) return 'no-position';
  if (mesh.matrixWorld.determinant() <= 0) return 'mirrored';
  if (vertexCount(mesh) > MERGE_MAX_MESH_VERTICES) return 'high-poly';
  return null;
}

function vertexCount(mesh: THREE.Mesh): number {
  return mesh.geometry.getAttribute('position').count;
}

export interface MergeBucket {
  material: THREE.Material;
  castShadow: boolean;
  receiveShadow: boolean;
  meshes: THREE.Mesh[];
}

export interface MergePlan {
  buckets: MergeBucket[];
  /** Meshes that stay live, by reason (a report, and what the tests pin). */
  kept: Record<string, number>;
}

function attributeSignature(geometry: THREE.BufferGeometry): string {
  const names = Object.keys(geometry.attributes).sort();
  const parts = names.map((n) => `${n}:${geometry.getAttribute(n).itemSize}`);
  return `${parts.join(',')}|${geometry.index ? 'i' : 'n'}`;
}

function inheritedBehaviour(node: THREE.Object3D, root: THREE.Object3D): string | null {
  for (let cur: THREE.Object3D | null = node; cur && cur !== root; cur = cur.parent) {
    const reason = behaviourReason(cur);
    if (reason) return reason;
  }
  return null;
}

/**
 * Plan the merge of `root`'s static leaves: pure bookkeeping, no geometry is
 * touched. Only buckets of two or more meshes are returned — a bucket of one
 * would cost a copy and save nothing.
 */
export function planStaticMerge(root: THREE.Object3D, cellSize = MERGE_CELL_METRES): MergePlan {
  root.updateMatrixWorld(true);
  const kept: Record<string, number> = {};
  const keep = (reason: string): void => {
    kept[reason] = (kept[reason] ?? 0) + 1;
  };
  const byKey = new Map<string, MergeBucket>();
  const position = new THREE.Vector3();
  root.traverse((node) => {
    if (node === root) return;
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const reason = inheritedBehaviour(mesh, root) ?? shapeReason(mesh);
    if (reason) {
      keep(reason);
      return;
    }
    const material = mesh.material as THREE.Material;
    position.setFromMatrixPosition(mesh.matrixWorld);
    const cell = `${Math.floor(position.x / cellSize)},${Math.floor(position.z / cellSize)}`;
    const key = `${cell}|${material.uuid}|${attributeSignature(mesh.geometry)}|${mesh.castShadow ? 1 : 0}${mesh.receiveShadow ? 1 : 0}`;
    const bucket = byKey.get(key);
    if (bucket) bucket.meshes.push(mesh);
    else
      byKey.set(key, {
        material,
        castShadow: mesh.castShadow,
        receiveShadow: mesh.receiveShadow,
        meshes: [mesh],
      });
  });
  const buckets: MergeBucket[] = [];
  const emit = (bucket: MergeBucket): void => {
    if (bucket.meshes.length >= 2) buckets.push(bucket);
    else keep('single');
  };
  for (const bucket of byKey.values()) {
    // Split at the vertex cap so no single commit can grow with the town.
    let chunk: MergeBucket = { ...bucket, meshes: [] };
    let verts = 0;
    for (const mesh of bucket.meshes) {
      const n = vertexCount(mesh);
      if (chunk.meshes.length > 0 && verts + n > MERGE_MAX_BUCKET_VERTICES) {
        emit(chunk);
        chunk = { ...bucket, meshes: [] };
        verts = 0;
      }
      chunk.meshes.push(mesh);
      verts += n;
    }
    emit(chunk);
  }
  return { buckets, kept };
}

/**
 * A float32, non-interleaved, non-normalised copy of `geometry` baked into
 * `matrix`. Delivered glTF may carry quantised (normalised integer) or
 * interleaved attributes; transforming those in place would write floats into
 * integers, so every attribute is read back through the denormalising getters.
 */
function bakedCopy(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(geometry.attributes)) {
    const src = geometry.getAttribute(name);
    const size = src.itemSize;
    const plain = src as THREE.BufferAttribute;
    const interleaved = (src as { isInterleavedBufferAttribute?: boolean })
      .isInterleavedBufferAttribute;
    if (!src.normalized && interleaved !== true && plain.array instanceof Float32Array) {
      // Fast path: already plain float32 — one native copy.
      out.setAttribute(
        name,
        new THREE.BufferAttribute(plain.array.slice(0, src.count * size), size),
      );
      continue;
    }
    const array = new Float32Array(src.count * size);
    for (let i = 0; i < src.count; i += 1) {
      array[i * size] = src.getX(i);
      if (size > 1) array[i * size + 1] = src.getY(i);
      if (size > 2) array[i * size + 2] = src.getZ(i);
      if (size > 3) array[i * size + 3] = src.getW(i);
    }
    out.setAttribute(name, new THREE.BufferAttribute(array, size));
  }
  if (geometry.index) {
    out.setIndex(new THREE.BufferAttribute(Uint32Array.from(geometry.index.array), 1));
  }
  out.applyMatrix4(matrix);
  return out;
}

/**
 * Commit one bucket under `root`: bake each member into `root`'s space, merge,
 * add the merged mesh, detach the members. Members' geometry and material are
 * SHARED with the loader's template, so they are detached, never disposed. The
 * merged geometry is owned by the new mesh (freed with the world). Returns null
 * (and changes nothing) if three refuses the merge.
 */
export function commitBucket(root: THREE.Object3D, bucket: MergeBucket): THREE.Mesh | null {
  root.updateMatrixWorld(true);
  const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const relative = new THREE.Matrix4();
  const parts = bucket.meshes.map((mesh) =>
    bakedCopy(mesh.geometry, relative.multiplyMatrices(toRoot, mesh.matrixWorld)),
  );
  const merged = mergeGeometries(parts, false);
  for (const part of parts) part.dispose();
  if (merged === null) return null;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  const mesh = new THREE.Mesh(merged, bucket.material);
  mesh.name = 'static-merge';
  mesh.castShadow = bucket.castShadow;
  mesh.receiveShadow = bucket.receiveShadow;
  mesh.userData.mergedFrom = bucket.meshes.length;
  for (const member of bucket.meshes) member.removeFromParent();
  root.add(mesh);
  return mesh;
}

/** Mesh count a subtree REPRESENTS: a merged mesh counts as the meshes it replaced. */
export function representedMeshCount(root: THREE.Object3D): number {
  let n = 0;
  root.traverse((node) => {
    if ((node as THREE.Mesh).isMesh) {
      const from = node.userData.mergedFrom;
      n += typeof from === 'number' && from > 0 ? from : 1;
    }
  });
  return n;
}

interface MergeJob {
  root: THREE.Object3D;
  buckets: MergeBucket[];
  next: number;
  live: () => boolean;
}

/**
 * Amortised commit (AD-158 §5): groups are enqueued as soon as they are placed
 * (drawn unmerged at once, so nothing waits on the merge) and `step` commits
 * buckets until the slice budget is spent. At least one bucket per call, so the
 * queue always drains. A job whose world was replaced (`live()` false) is dropped.
 */
export class StaticMergeQueue {
  private jobs: MergeJob[] = [];
  /** Longest single bucket commit seen (ms) — the E3.6 Worker trigger's reading. */
  maxCommitMs = 0;
  merged = 0;

  constructor(private readonly now: () => number = () => performance.now()) {}

  enqueue(root: THREE.Object3D, live: () => boolean, cellSize = MERGE_CELL_METRES): void {
    const { buckets } = planStaticMerge(root, cellSize);
    if (buckets.length > 0) this.jobs.push({ root, buckets, next: 0, live });
  }

  /** Buckets still waiting to be committed. */
  pending(): number {
    let n = 0;
    for (const job of this.jobs) n += job.buckets.length - job.next;
    return n;
  }

  step(budgetMs = MERGE_SLICE_MS): void {
    const start = this.now();
    let committed = 0;
    while (this.jobs.length > 0) {
      const job = this.jobs[0] as MergeJob;
      if (!job.live() || job.next >= job.buckets.length) {
        this.jobs.shift();
        continue;
      }
      if (committed > 0 && this.now() - start >= budgetMs) return;
      const t0 = this.now();
      if (commitBucket(job.root, job.buckets[job.next] as MergeBucket)) this.merged += 1;
      this.maxCommitMs = Math.max(this.maxCommitMs, this.now() - t0);
      job.next += 1;
      committed += 1;
    }
  }

  clear(): void {
    this.jobs = [];
  }
}
